/**
 * Renders an AND/OR group of conditions. Recursive — children may themselves
 * be groups, NOT-wrappers, or leaf condition rows.
 */
import React from "react"
import { Button, Segmented, Tooltip } from "antd"
import { PlusOutlined, GroupOutlined, DeleteOutlined } from "@ant-design/icons"

import { LabelItem } from "utils/labelUtils"
import {
  ASTNode,
  GroupNode,
  GroupOp,
  NotNode,
  PlaceholderNode,
} from "utils/logicWizard/grammar"
import { QuestionInfo } from "utils/logicWizard/questionTypeDetector"
import ConditionRow from "./ConditionRow.component"

interface ConditionGroupProps {
  /** This node MUST be a Group (root or nested). */
  node: GroupNode
  labels: LabelItem[]
  questionInfo: Map<string, QuestionInfo>
  listMap: ReadonlyMap<string, LabelItem>
  /** Replace this group's content. */
  onChange: (next: ASTNode) => void
  /** Optional: callback to delete this group entirely. Hidden on root. */
  onDelete?: () => void
  /** Indentation depth for visual nesting. */
  depth?: number
}

const placeholder: PlaceholderNode = { kind: "placeholder" }

const ConditionGroup: React.FC<ConditionGroupProps> = ({
  node,
  labels,
  questionInfo,
  listMap,
  onChange,
  onDelete,
  depth = 0,
}) => {
  const replaceChild = (idx: number, next: ASTNode) => {
    const children = [...node.children]
    children[idx] = next
    onChange({ ...node, children })
  }

  const removeChild = (idx: number) => {
    const children = node.children.filter((_, i) => i !== idx)
    onChange({ ...node, children })
  }

  const addCondition = () => {
    onChange({
      ...node,
      children: [...node.children, placeholder],
    })
  }

  const addGroup = () => {
    const newGroup: GroupNode = {
      kind: "group",
      op: node.op === "AND" ? "OR" : "AND",
      children: [placeholder],
    }
    onChange({ ...node, children: [...node.children, newGroup] })
  }

  const setOp = (op: GroupOp) => {
    onChange({ ...node, op })
  }

  return (
    <div
      className="border rounded p-3 bg-gray-50"
      style={{ marginLeft: depth > 0 ? 16 : 0 }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Segmented
          size="small"
          value={node.op}
          onChange={(v) => setOp(v as GroupOp)}
          options={[
            { label: "AND (all true)", value: "AND" },
            { label: "OR (any true)", value: "OR" },
          ]}
        />
        <span className="text-xs text-gray-500">
          {node.children.length === 0
            ? "no conditions yet"
            : `${node.children.length} condition${node.children.length === 1 ? "" : "s"}`}
        </span>
        {onDelete && (
          <Tooltip title="Remove this group">
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={onDelete}
              className="ml-auto"
            />
          </Tooltip>
        )}
      </div>

      <div className="flex flex-col gap-1">
        {node.children.map((child, idx) => (
          <ChildSlot
            key={idx}
            child={child}
            labels={labels}
            questionInfo={questionInfo}
            listMap={listMap}
            depth={depth + 1}
            onChange={(c) => replaceChild(idx, c)}
            onDelete={() => removeChild(idx)}
          />
        ))}
      </div>

      <div className="flex items-center gap-2 mt-2">
        <Button
          size="small"
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addCondition}
        >
          Add condition
        </Button>
        <Button
          size="small"
          type="dashed"
          icon={<GroupOutlined />}
          onClick={addGroup}
        >
          Add group ({node.op === "AND" ? "OR" : "AND"})
        </Button>
      </div>
    </div>
  )
}

interface ChildSlotProps {
  child: ASTNode
  labels: LabelItem[]
  questionInfo: Map<string, QuestionInfo>
  listMap: ReadonlyMap<string, LabelItem>
  depth: number
  onChange: (next: ASTNode) => void
  onDelete: () => void
}

/**
 * Dispatches rendering based on child kind. Handles NOT-wrappers, groups,
 * and leaf rows.
 */
const ChildSlot: React.FC<ChildSlotProps> = ({
  child,
  labels,
  questionInfo,
  listMap,
  depth,
  onChange,
  onDelete,
}) => {
  if (child.kind === "group") {
    return (
      <ConditionGroup
        node={child}
        labels={labels}
        questionInfo={questionInfo}
        listMap={listMap}
        onChange={onChange}
        onDelete={onDelete}
        depth={depth}
      />
    )
  }
  if (child.kind === "not") {
    const inner = child.child
    return (
      <div className="flex items-center gap-2 border-l-2 border-orange-300 pl-2">
        <Tooltip title="Remove NOT. Condition fires when the inner expression is true">
          <Button
            size="small"
            danger
            onClick={() => onChange(inner)}
            style={{ flexShrink: 0 }}
          >
            NOT ✕
          </Button>
        </Tooltip>
        <div className="flex-1">
          {inner.kind === "group" ? (
            <ConditionGroup
              node={inner}
              labels={labels}
              questionInfo={questionInfo}
              listMap={listMap}
              onChange={(n) =>
                onChange({ kind: "not", child: n } satisfies NotNode)
              }
              depth={depth}
            />
          ) : (
            <ConditionRow
              node={inner}
              labels={labels}
              questionInfo={questionInfo}
              listMap={listMap}
              onChange={(n) =>
                onChange({ kind: "not", child: n } satisfies NotNode)
              }
              onDelete={onDelete}
            />
          )}
        </div>
      </div>
    )
  }
  return (
    <ConditionRow
      node={child}
      labels={labels}
      questionInfo={questionInfo}
      listMap={listMap}
      onChange={onChange}
      onDelete={onDelete}
      onWrapInNot={() =>
        onChange({ kind: "not", child } satisfies NotNode)
      }
    />
  )
}

export default ConditionGroup
